/* CATCORNS Shopify Online Store 2.0 theme — storefront interactions.
   The Shopify cart API is the authority for availability, prices and discounts. */
(() => {
  'use strict';

  const settings = window.Catcorns || {};
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || settings.root || '/';
  const api = (path) => root.replace(/\/?$/, '/') + path.replace(/^\//, '');
  const $ = (selector, parent = document) => parent.querySelector(selector);
  const $$ = (selector, parent = document) => Array.from(parent.querySelectorAll(selector));
  const announce = (message) => { const node = $('#cc-announcer'); if (node) node.textContent = message; };

  function money(cents) {
    const amount = Number(cents) || 0;
    const format = String(settings.moneyFormat || '{{amount}}');
    const sign = amount < 0 ? '-' : '';
    const abs = Math.abs(amount);
    function numberString(decimals, thousands, decimal) {
      const parts = (abs / 100).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[0] + (parts.length > 1 ? decimal + parts[1] : '');
    }
    const replacements = {
      amount: numberString(2, ',', '.'),
      amount_no_decimals: numberString(0, ',', '.'),
      amount_with_comma_separator: numberString(2, '.', ','),
      amount_no_decimals_with_comma_separator: numberString(0, '.', ','),
      amount_with_apostrophe_separator: numberString(2, "'", '.'),
      amount_with_space_separator: numberString(2, ' ', ','),
      amount_no_decimals_with_space_separator: numberString(0, ' ', ',')
    };
    return sign + format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => replacements[key] || replacements.amount);
  }

  async function requestCart(endpoint, method = 'GET', body) {
    const response = await fetch(api(endpoint), {
      method,
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    let json;
    try { json = await response.json(); } catch (error) { throw new Error('The store could not complete this request. Please try again.'); }
    if (!response.ok) {
      const msg = json.description || json.message || 'Something went wrong. Please try again.';
      throw new Error(typeof msg === 'string' ? msg : 'The item could not be added.');
    }
    return json;
  }

  function create(tag, classes, text) {
    const el = document.createElement(tag);
    if (classes) el.className = classes;
    if (text !== undefined) el.textContent = text;
    return el;
  }


  // Helpers are deliberately pure: the same variant selection can be validated in tests.
  function matchVariant(variants, options) {
    return variants.find((variant) => variant.options.length === options.length &&
      variant.options.every((val, idx) => String(val) === String(options[idx])));
  }
  function stockStatus(selectedVariants) {
    const counts = new Map();
    for (const variant of selectedVariants) {
      if (!variant || !variant.available) return { ok: false, message: 'That design and size are unavailable.' };
      const id = String(variant.id);
      counts.set(id, (counts.get(id) || 0) + 1);
      if (variant.inventoryManagement && variant.inventoryPolicy === 'deny' &&
          counts.get(id) > Number(variant.inventoryQuantity || 0)) {
        return { ok: false, message: 'There are not enough of that design and size in stock for this bundle.' };
      }
    }
    return { ok: true, message: '' };
  }
  function buildLineItems(selectedVariants) {
    const quantities = new Map();
    selectedVariants.forEach((v) => quantities.set(v.id, (quantities.get(v.id) || 0) + 1));
    return Array.from(quantities, ([id, quantity]) => ({ id: String(id), quantity }));
  }

  const Drawer = {
    drawer: null,
    overlay: null,
    focusBefore: null,
    busy: false,
    init() {
      this.drawer = $('[data-cart-drawer]');
      this.overlay = $('[data-cart-overlay]');
      if (!this.drawer || !this.overlay) return;
      $$('[data-cart-toggle]').forEach((trigger) => trigger.addEventListener('click', async () => {
        this.open();
        await this.refresh();
      }));
      this.overlay.addEventListener('click', () => this.close());
      $('[data-cart-close]', this.drawer)?.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen()) this.close();
        if (event.key === 'Tab' && this.isOpen()) {
          const list = $$('a[href],button:not(:disabled),input:not(:disabled)', this.drawer).filter((e) => e.offsetParent !== null);
          if (!list.length) return;
          const first = list[0], last = list[list.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }
      });
    },
    isOpen() { return this.drawer?.classList.contains('is-open') || false; },
    open() {
      if (!this.drawer) return;
      if (!this.isOpen()) this.focusBefore = document.activeElement;
      this.drawer.classList.add('is-open');
      this.drawer.removeAttribute('inert');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.overlay.hidden = false;
      document.body.classList.add('cc-no-scroll');
      $('[data-cart-close]', this.drawer)?.focus();
    },
    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawer.setAttribute('inert', '');
      this.overlay.hidden = true;
      document.body.classList.remove('cc-no-scroll');
      this.focusBefore?.focus?.();
    },
    async refresh() {
      try { const cart = await requestCart('cart.js'); this.render(cart); return cart; }
      catch (error) { announce(error.message); return null; }
    },
    render(cart) {
      $$('[data-cart-count]').forEach((badge) => {
        badge.textContent = String(cart.item_count);
        badge.hidden = cart.item_count < 1;
      });
      const count = $('[data-cart-drawer-count]', this.drawer);
      if (count) count.textContent = `(${cart.item_count})`;
      const subtotal = $('[data-cart-subtotal]', this.drawer);
      if (subtotal) subtotal.textContent = money(cart.total_price);
      const saved = $('[data-cart-savings]', this.drawer);
      if (saved) {
        const savings = Math.max(0, cart.original_total_price - cart.total_price);
        saved.hidden = savings === 0;
        saved.textContent = savings > 0 ? `Shopify discounts applied: ${money(savings)} saved` : '';
      }
      const shipping = $('[data-cart-shipping]', this.drawer);
      const threshold = Number(settings.shippingThreshold || 0);
      if (shipping) {
        shipping.hidden = threshold <= 0;
        if (threshold > 0) {
          const remaining = Math.max(0, threshold - cart.total_price);
          shipping.textContent = remaining > 0 ? `${money(remaining)} away from free shipping` : 'Your order meets the free-shipping threshold!';
        }
      }
      const list = $('[data-cart-items]', this.drawer);
      if (!list) return;
      list.replaceChildren();
      if (!cart.items || cart.items.length === 0) {
        const empty = create('div', 'cc-cart-empty');
        empty.append(create('div', 'cc-empty-heart', '♡'), create('h3', '', 'Your bag is waiting for something cozy.'), create('p', '', 'Your new favorite might be just one click away.'));
        const shopLink = create('a', 'cc-btn cc-btn-secondary', 'CONTINUE SHOPPING');
        shopLink.href = api('collections/all');
        empty.append(shopLink);
        list.append(empty);
        return;
      }
      cart.items.forEach((item) => {
        const line = create('div', 'cc-drawer-line');
        const imageLink = create('a', 'cc-drawer-line-image');
        imageLink.href = item.url || '#';
        const imgSrc = item.featured_image?.url || item.image;
        if (imgSrc) {
          const img = create('img');
          img.src = imgSrc;
          img.alt = item.product_title || 'Product photo';
          img.loading = 'lazy';
          imageLink.append(img);
        } else imageLink.append(create('span', '', '♥'));
        const info = create('div', 'cc-drawer-line-info');
        const title = create('a', 'cc-drawer-line-title', item.product_title || item.title);
        title.href = item.url || '#';
        info.append(title);
        if (item.variant_title && item.variant_title !== 'Default Title') info.append(create('small', 'cc-drawer-variant', item.variant_title));
        Object.entries(item.properties || {}).forEach(([name,value])=>{if(value && !name.startsWith('_'))info.append(create('small','cc-drawer-property',name+': '+value));});
        const row = create('div', 'cc-drawer-line-bottom');
        const qty = create('div', 'cc-drawer-qty');
        const minus = create('button', '', '−');
        minus.type = 'button'; minus.setAttribute('aria-label', `Remove one ${item.product_title}`);
        const amount = create('input');
        amount.type = 'number'; amount.min = '0'; amount.max = '99'; amount.step = '1'; amount.value = String(item.quantity);
        amount.setAttribute('aria-label', `Quantity of ${item.product_title}`);
        const plus = create('button', '', '+'); plus.type = 'button'; plus.setAttribute('aria-label', `Add one ${item.product_title}`);
        minus.addEventListener('click', () => this.change(item.key, item.quantity - 1));
        plus.addEventListener('click', () => this.change(item.key, item.quantity + 1));
        amount.addEventListener('change', () => {
          const next = Number.parseInt(amount.value, 10);
          if (!Number.isInteger(next) || next < 0 || next > 99) { amount.value = String(item.quantity); return; }
          this.change(item.key, next);
        });
        qty.append(minus, amount, plus);
        const price = create('strong', '', money(item.final_line_price));
        const remove = create('button', 'cc-line-remove', 'Remove');
        remove.type = 'button'; remove.addEventListener('click', () => this.change(item.key, 0));
        row.append(qty, price);
        info.append(row, remove);
        line.append(imageLink, info);
        list.append(line);
      });
    },
    async change(key, quantity) {
      if (this.busy) return;
      this.busy = true;
      this.drawer?.classList.add('is-busy');
      try {
        const cart = await requestCart('cart/change.js', 'POST', { id: key, quantity });
        this.render(cart);
        announce('Shopping bag updated.');
      } catch (error) { announce(error.message); await this.refresh(); }
      finally { this.busy = false; this.drawer?.classList.remove('is-busy'); }
    },
    async add(items, open = true) {
      await requestCart('cart/add.js', 'POST', { items });
      await this.refresh();
      if (open) this.open();
      announce('Added to your shopping bag.');
    }
  };

  function initHeader() {
    $$('[data-cc-header]').forEach((header) => {
      if (header.dataset.ccReady) return;
      header.dataset.ccReady = 'true';
      const mobileButton = $('[data-mobile-menu-toggle]', header);
      const mobileMenu = $('#cc-mobile-menu', header);
      mobileButton?.addEventListener('click', () => {
        const open = mobileMenu.hidden;
        mobileMenu.hidden = !open;
        mobileButton.setAttribute('aria-expanded', String(open));
        mobileButton.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      });
      const searchButton = $('[data-search-toggle]', header);
      const searchPanel = $('#cc-header-search', header);
      searchButton?.addEventListener('click', () => {
        const open = searchPanel.hidden;
        searchPanel.hidden = !open;
        searchButton.setAttribute('aria-expanded', String(open));
        if (open) $('input[type=search]', searchPanel)?.focus();
      });
    });
  }

  function initProduct(section) {
    if (section.dataset.ccProductReady) return;
    const json = $('[data-cc-product-json]', section);
    if (!json) return;
    let data;
    try { data = JSON.parse(json.textContent); } catch (error) { return; }
    if (!data.variants || !data.variants.length) return;
    section.dataset.ccProductReady = 'true';
    const form = $('.cc-product-form', section);
    const choices = $('[data-bundle-choices]', section);
    const total = $('[data-bundle-total]', section);
    const mobileTotal = $('[data-mobile-total]', section);
    const mainPrice = $('[data-product-price]', section);
    const compare = $('[data-product-compare]', section);
    const status = $('[data-stock-status]', section);
    const error = $('[data-product-error]', section);
    const add = $('[data-cc-add]', section);
    const buyNow = $('[data-cc-buy-now]', section);
    const sticky = $('[data-cc-sticky-add]', section);
    const variantHidden = $('[data-product-variant-input]', section);
    const mainImage = $('.cc-main-product-image', section);
    const initially = data.variants.find((v) => String(v.id) === String(data.initialVariantId)) || data.variants[0];
    let numberOfHoodies = 1;
    let selections = [initially.options.slice()];
    let resolved = [];
    let inProgress = false;

    const matching = (options) => matchVariant(data.variants, options);
    // Keeping all three choices lets shoppers switch between Buy 1 / 2 / 3 without losing their picks.
    function defaultOptions(index) {
      if (index === 0) return initially.options.slice();
      const firstOption = data.options[0];
      if (!firstOption || !firstOption.values.length) return initially.options.slice();
      const target = firstOption.values[index % firstOption.values.length];
      const found = data.variants.find((v) => v.available && String(v.options[0]) === String(target));
      return (found || initially).options.slice();
    }
    selections = [defaultOptions(0), defaultOptions(1), defaultOptions(2)];
    const inStockPrices = data.variants.filter((v) => v.available);
    const minPrice = Math.min(...(inStockPrices.length ? inStockPrices : data.variants).map((v) => Number(v.price) || 0));
    function showError(message) { error.textContent = message || ''; error.hidden = !message; if (message) announce(message); }
    function setImage(src) {
      if (!mainImage || !src) return;
      if (mainImage.src !== src) {
        mainImage.src = src;
        mainImage.removeAttribute('srcset'); mainImage.removeAttribute('sizes');
      }
      const lightboxImage = $('[data-lightbox-image]', section);
      if (lightboxImage) lightboxImage.src = src;
    }
    function showSelectedCard() {
      $$('[data-bundle-card]', section).forEach((card) => card.classList.toggle('is-selected', Number(card.dataset.bundleCard) === numberOfHoodies));
    }
    function update() {
      resolved = selections.slice(0, numberOfHoodies).map(matching);
      const availability = stockStatus(resolved);
      const valid = availability.ok && resolved.length === numberOfHoodies;
      const allMatched = resolved.every(Boolean);
      const subtotal = resolved.reduce((sum, variant) => sum + (variant?.price || 0), 0);
      if (total) total.textContent = allMatched ? money(subtotal) : 'Select a design and size';
      const label = $('[data-bundle-total-label]', section);
      if (label) label.textContent = numberOfHoodies === 1 ? '1 hoodie' : `${numberOfHoodies} hoodies`;
      $$('[data-bundle-card-price]', section).forEach((node) => {
        const count = Number(node.dataset.bundleCardPrice);
        node.textContent = count === numberOfHoodies && allMatched ? money(subtotal) : `From ${money(minPrice * count)}`;
      });
      if (mobileTotal) mobileTotal.textContent = allMatched ? money(subtotal) : 'Choose options';
      const first = resolved[0] || initially;
      if (mainPrice) mainPrice.textContent = money(first.price);
      if (compare) {
        const compareValue = Number(first.compareAtPrice || 0);
        compare.hidden = !(compareValue > first.price);
        compare.textContent = compareValue > first.price ? money(compareValue) : '';
      }
      if (status) {
        status.textContent = valid ? 'Available' : 'Check your options';
        status.classList.toggle('is-unavailable', !valid);
      }
      [add, buyNow, sticky].forEach((button) => { if (button) button.disabled = !valid || inProgress; });
      const addLabel = $('[data-add-label]', add);
      if (addLabel) addLabel.textContent = valid ? 'ADD TO BAG' : 'UNAVAILABLE';
      if (first) {
        if (variantHidden) variantHidden.value = String(first.id);
        setImage(first.image);
        try {
          const url = new URL(window.location.href);
          if (url.searchParams.has('variant') || window.location.pathname.includes('/products/')) {
            url.searchParams.set('variant', String(first.id));
            window.history.replaceState({}, '', url.pathname + url.search + url.hash);
          }
        } catch (ignored) { /* Updating the address bar is optional. */ }
      }
      $$('.cc-picker-status', choices).forEach((node, i) => {
        const v = resolved[i];
        node.textContent = !v ? 'Choose another combination.' : !v.available ? 'This option is sold out.' :
          !availability.ok ? availability.message : '';
      });
      $$('[data-hoodie-line-price]', choices).forEach((node, i) => {
        node.textContent = resolved[i] ? money(resolved[i].price) : '';
      });
    }
    function choose(index, optionIndex, value) {
      selections[index][optionIndex] = String(value);
      const desired = matching(selections[index]);
      const changedName = String(data.options[optionIndex]?.name || '').toLowerCase();
      const designChange = /design|style|character|pattern|color|colour/.test(changedName) || optionIndex === 0;
      if ((!desired || !desired.available) && designChange) {
        // Only change another option automatically when the shopper selects a new design.
        const alternate = data.variants.find((v) => v.available && String(v.options[optionIndex]) === String(value));
        if (alternate) selections[index] = alternate.options.slice();
      }
      showError('');
      draw({ index, optionIndex, value });
    }
    function draw(focus) {
      choices.replaceChildren();
      for (let i = 0; i < numberOfHoodies; i += 1) {
        const item = create('fieldset', 'cc-picker-row');
        const title = create('legend', 'cc-picker-title', numberOfHoodies === 1 ? 'YOUR HOODIE' : `HOODIE ${i + 1}`);
        item.append(title);
        const rowHead = create('div', 'cc-picker-head');
        rowHead.append(create('span', '', numberOfHoodies === 1 ? 'Make it yours' : `Customize hoodie ${i + 1}`));
        const rowPrice = create('strong', 'cc-picker-line-price');
        rowPrice.dataset.hoodieLinePrice = String(i);
        rowHead.append(rowPrice);
        item.append(rowHead);
        const fields = create('div', 'cc-picker-fields');
        data.options.forEach((option, j) => {
          const label = create('div', 'cc-variant-field');
          label.append(create('span', 'cc-choice-label', option.name));
          const lower = String(option.name).toLowerCase();
          const isDesign = /design|style|character|pattern|color|colour/.test(lower) || (j === 0 && data.options.length > 1);
          const isSize = /size|fit/.test(lower);
          const asButtons = option.values.length <= 12 && (isDesign || isSize);
          if (asButtons) {
            const swatches = create('div', isDesign ? 'cc-design-options' : 'cc-size-options');
            option.values.forEach((value) => {
              const v = String(value);
              const test = data.variants.filter((variant) => String(variant.options[j]) === v);
              const designIndex = data.options.findIndex((opt) => /design|style|character|pattern|color|colour/.test(String(opt.name).toLowerCase()));
              const fixedDesignIndex = designIndex >= 0 ? designIndex : 0;
              const available = test.some((variant) => variant.available &&
                (j === fixedDesignIndex || String(variant.options[fixedDesignIndex]) === String(selections[i][fixedDesignIndex])));
              const imgVariant = test.find((variant) => variant.image && variant.available) || test.find((variant) => variant.image);
              const choice = create('label', isDesign ? 'cc-design-choice' : 'cc-size-choice');
              const radio = create('input');
              radio.type = 'radio';
              radio.name = `cc_${section.dataset.productSection}_${i}_${j}`;
              radio.value = v;
              radio.checked = String(selections[i][j]) === v;
              radio.disabled = !available;
              radio.dataset.pickerKey = `${i}-${j}`;
              choice.classList.toggle('is-picked', radio.checked);
              choice.classList.toggle('is-sold-out', !available);
              radio.addEventListener('change', () => choose(i, j, v));
              choice.append(radio);
              if (isDesign) {
                const preview = create('span', 'cc-choice-photo');
                if (imgVariant?.image) {
                  const img = create('img'); img.src = imgVariant.thumb || imgVariant.image; img.alt = ''; img.loading = 'lazy';
                  preview.append(img);
                } else preview.textContent = '♡';
                choice.append(preview);
              }
              choice.append(create('span', 'cc-choice-name', v));
              if (!available) choice.append(create('small', 'cc-choice-unavailable', 'Sold out'));
              swatches.append(choice);
            });
            label.append(swatches);
          } else {
            const select = create('select', 'cc-variant-select');
            select.setAttribute('aria-label', `Hoodie ${i + 1}: ${option.name}`);
            select.dataset.pickerKey = `${i}-${j}`;
            option.values.forEach((val) => {
              const opt = create('option', '', String(val));
              opt.value = String(val);
              opt.selected = String(val) === String(selections[i][j]);
              select.append(opt);
            });
            select.addEventListener('change', () => choose(i, j, select.value));
            label.append(select);
          }
          fields.append(label);
        });
        if (!data.options.length) fields.append(create('p', 'cc-variant-field', 'One size · ready to cuddle'));
        const availability = create('p', 'cc-picker-status');
        item.append(fields, availability);
        choices.append(item);
      }
      showSelectedCard(); update();
      if (focus) {
        const control = $$('[data-picker-key]', choices).find((node) =>
          node.dataset.pickerKey === `${focus.index}-${focus.optionIndex}` &&
          (node.tagName === 'SELECT' || node.value === focus.value));
        control?.focus({ preventScroll: true });
      }
    }
    $$('input[type=radio][name^=cc_bundle_qty_]', section).forEach((radio) => {
      radio.addEventListener('change', () => {
        numberOfHoodies = Number(radio.value);
        showError(''); draw();
      });
    });
    async function addSelection(checkout = false) {
      if (inProgress) return;
      update();
      const availability = stockStatus(resolved);
      if (resolved.length !== numberOfHoodies || !availability.ok) {
        showError(availability.message || 'Choose an available design and size for every hoodie.');
        return;
      }
      inProgress = true;
      [add, buyNow, sticky].forEach((button) => { if (button) button.disabled = true; });
      const addLabel = $('[data-add-label]', add);
      if (addLabel) addLabel.textContent = 'ADDING…';
      showError('');
      try {
        await Drawer.add(buildLineItems(resolved), !checkout);
        if (checkout) window.location.assign(api('checkout'));
      } catch (cause) {
        showError(cause.message || 'Unable to add this bundle. Check your selections and try again.');
      } finally {
        inProgress = false;
        update();
      }
    }
    form.addEventListener('submit', (event) => { event.preventDefault(); addSelection(false); });
    buyNow?.addEventListener('click', () => addSelection(true));
    sticky?.addEventListener('click', () => addSelection(false));
    $$('[data-gallery-thumb]', section).forEach((thumb) => thumb.addEventListener('click', () => {
      setImage(thumb.dataset.imageSrc);
      if (mainImage) mainImage.alt = thumb.dataset.imageAlt || data.title;
      $$('[data-gallery-thumb]', section).forEach((node) => node.classList.toggle('is-active', node === thumb));
    }));
    const lightbox = $('[data-cc-lightbox]', section);
    $('[data-image-zoom]', section)?.addEventListener('click', () => {
      const lightboxImage = $('[data-lightbox-image]', lightbox);
      if (lightboxImage && mainImage) lightboxImage.src = mainImage.src;
      if (lightbox?.showModal) lightbox.showModal();
    });
    $('[data-lightbox-close]', section)?.addEventListener('click', () => lightbox?.close());
    lightbox?.addEventListener('click', (event) => { if (event.target === lightbox) lightbox.close(); });
    const mobileSticky = $('[data-cc-mobile-sticky]', section);
    if (mobileSticky) {
      // Display only once the product controls are above the viewport.
      const updateSticky = () => mobileSticky.classList.toggle('is-visible', form.getBoundingClientRect().bottom < 0);
      window.addEventListener('scroll', updateSticky, { passive: true });
      window.addEventListener('resize', updateSticky, { passive: true });
      updateSticky();
    }
    draw();
  }

  function initGeneric() {
    $$('[data-auto-submit]').forEach((select) => {
      if (select.dataset.ccReady) return;
      select.dataset.ccReady = 'true';
      select.addEventListener('change', () => select.form?.requestSubmit ? select.form.requestSubmit() : select.form?.submit());
    });
    $$('[data-quick-add]').forEach((button) => {
      if (button.dataset.ccReady) return;
      button.dataset.ccReady = 'true';
      button.addEventListener('click', async () => {
        button.disabled = true;
        const before = button.textContent;
        button.textContent = 'ADDING…';
        try { await Drawer.add([{ id: String(button.dataset.quickAdd), quantity: 1 }]); }
        catch (error) { announce(error.message); window.alert(error.message); }
        finally { button.disabled = false; button.textContent = before; }
      });
    });
  }

  function init(scope = document) {
    initHeader();
    $$('[data-cc-product]', scope).forEach(initProduct);
    if (scope.matches?.('[data-cc-product]')) initProduct(scope);
    initGeneric();
  }
  document.addEventListener('DOMContentLoaded', () => {
    Drawer.init();
    init();
    Drawer.refresh();
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  window.CatcornsStore = {
    refreshCart: () => Drawer.refresh(), formatMoney: money,
    matchVariant, stockStatus, buildLineItems
  };
})();
